// Branded print / PDF engine. Opens a print window with the business branding
// header and "Powered by NEEC POS" footer, then triggers the browser print dialog
// (users can Save as PDF).
import { parseDate } from "./format";
import { diagPrint, diagPrintDuration } from "./diag";


export const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
// Escapes a string for safe use inside a CSS `content:"..."` value (e.g. an @page margin-box footer).
const cssStr = (s) => String(s ?? "").replace(/\\/g, "\\\\").replace(/"/g, '\\"');

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (v) => { if (!v) return null; if (/^https?:\/\//i.test(v) || v.startsWith("data:")) return v; if (v.startsWith("/api/")) return `${BACKEND}${v}`; return v; };

export const pnum = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const pmoney = (n, cur = "JMD $") => `${cur}${pnum(n)}`;
// Currency code from the business setting: "JMD $" -> "JMD", "USD $" -> "USD", "$" -> "$".
export const curCode = (cur = "JMD $") => { const c = String(cur).replace(/\$/g, "").trim(); return c || String(cur).trim() || "JMD"; };
// Receipt totals money: "JMD 10,600.00" (code + space, no symbol).
export const rmoney = (n, cur = "JMD $") => `${curCode(cur)} ${pnum(n)}`;
const numTd = (n, extra = "") => { const t = pnum(n); return `<td class="right num ${t.length >= 9 ? "l" : ""} ${extra}">${t}</td>`; };
export const bulkSavings = (items = []) => items.reduce((a, i) => a + (i.pricing_source === "bulk_tier" && i.original_unit_price != null ? (i.original_unit_price - (i.applied_unit_price ?? i.price)) * i.quantity : 0), 0);

// plain=true: internal filed record (e.g. Shift Report) — no sale-receipt footer / return policy; thermal also drops contact lines.
// inline=true: print from a hidden same-tab iframe (dialog is modal to THIS tab, no popup to block / lose); falls back to the popup.
// onDone(event): called in the POS when the receipt window reports "afterprint" / "closed" (focus recovery hook).
// `_preview = true` (Stage 3 Preview Quote, internal-only — not part of the public
// printDoc contract) builds and returns the exact same HTML this function would
// otherwise print, WITHOUT the trailing window.print() trigger and without opening
// any window/tab. Every existing caller omits it and is 100% unaffected.
// `footerNote` / `pageNumbers` (standard/A4 template only — R1-04A, multi-page reports):
// opt-in repeating page footer rendered via native CSS @page margin boxes, so it appears
// on EVERY printed page (not just the last). Omitted by every existing caller — zero
// effect on receipts, invoices, statements or any other document already in production.
// `win` (optional, standard/A4 template only — R1-04B fix, 2026-10-29): a window reference the
// caller already opened SYNCHRONOUSLY inside the click handler, before doing any `await`. When
// given, printDoc writes the final HTML into that window instead of calling `window.open()`
// itself. This matters because `window.open()` called *after* an `await` (e.g. a data fetch) is
// no longer inside the original click's trusted user-activation window — some browsers (notably
// Chrome/Edge on Windows) then silently drop the embedded `window.print()` call once the window
// loads, so the report renders in a plain viewer tab but the OS/browser Print dialog never opens.
// Every existing caller omits `win` and keeps calling `printDoc` synchronously with already-loaded
// data (e.g. the Business Report) — zero effect on them.
// `printButton` (opt-in, standard/A4 template only — R1-04B targeted fix, 2026-10-29): renders a
// visible "Print Now" button inside the printed document itself, so the end user can trigger
// window.print() from a click that happens ENTIRELY inside the already-open report window/tab —
// the most trustworthy possible print trigger, independent of how that window/tab was opened
// (popup timing, an embedded/sandboxed preview frame, etc.). Hidden from the actual printed
// page/PDF via @media print. The existing window.onload auto-print attempt is left in place as a
// convenience for browsers where it still works; this button is the guaranteed fallback. Omitted
// by every existing caller — zero effect on receipts, invoices, statements or any other document.
// `wrapTitle` (opt-in, standard/A4 template only — Reports Final UI Polish fix): lets a long
// interpolated doc title (e.g. a tenant's own custom wallet name baked into "{Wallet Name} Report")
// wrap onto multiple lines instead of overflowing past the page edge and squeezing the business
// name/address column down to nothing. Every existing caller omits it and renders byte-identical —
// zero effect on receipts, invoices, statements, Business/Products/Purchasing/Custom Report prints.
export function printDoc({ business = {}, docTitle = "Document", docRef = "", meta = [], body = "", accent = "#10b981", showBank = false, format = "standard", receipt = false, plain = false, inline = false, onDone = null, _preview = false, footerNote = null, pageNumbers = false, win = null, printButton = false, wrapTitle = false }) {
  const b = business;
  const thermal = format === "thermal80" || format === "thermal58";
  const showLogo = b.show_logo_on_receipts !== false;
  const colorLogo = absUrl(b.logo_url);
  const monoLogo = absUrl(b.logo_thermal_url) || colorLogo;

  // Receipts ALWAYS use the single shared stacked template (preview + 58mm + 80mm),
  // never the A4 split-header layout. The A4 template is only for statements/invoices.
  if (thermal || receipt) return printThermal({ b, docTitle, docRef, meta, body, format, showLogo, monoLogo, plain, inline, onDone, _preview });

  // Standard / PDF — logo on a neutral white surface so any colour stays visible.
  // The logo box is a fixed, non-shrinking column and the <img> is pinned to it (flex items default to
  // min-width:auto, which let a large logo bleed over the business details).
  const logo = (showLogo && colorLogo)
    ? `<div class="logo" style="border:1px solid #e2e8f0;background:#fff;padding:6px"><img src="${esc(colorLogo)}" alt="" style="display:block;width:100%;height:100%;min-width:0;object-fit:contain" /></div>`
    : `<div class="logo" style="background:${accent};color:#fff;font-size:30px;font-weight:800">${esc((b.name || "N")[0])}</div>`;

  const metaHtml = meta.map((m) => `<tr><td style="color:#64748b;padding:2px 16px 2px 0">${esc(m[0])}</td><td style="text-align:right;font-weight:600">${esc(m[1])}</td></tr>`).join("");
  const bank = b.bank_info || {};
  const recv = b.receipt_info || {};

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(docTitle)} ${esc(docRef)}</title>
  <style>
    *{box-sizing:border-box;font-family:'Helvetica Neue',Arial,sans-serif}
    body{margin:0;color:#0f172a;background:#fff}
    .page{max-width:800px;margin:0 auto;padding:40px}
    .head{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;border-bottom:3px solid ${accent};padding-bottom:20px}
    .biz{display:flex;gap:14px;align-items:flex-start;min-width:0;flex:1 1 auto}
    .biz>div{min-width:0}
    .logo{flex:0 0 74px;width:74px;height:74px;border-radius:14px;display:flex;align-items:center;justify-content:center;overflow:hidden}
    .biz h1{margin:0;font-size:22px;overflow-wrap:anywhere}
    .biz p{margin:2px 0;font-size:12px;color:#475569;overflow-wrap:anywhere}
    .doc{text-align:right;flex:0 0 auto}
    .doc h2{margin:0;font-size:26px;color:${accent};text-transform:uppercase;letter-spacing:1px}
    .doc .ref{font-family:monospace;font-size:14px;margin-top:4px}
    table.data{width:100%;border-collapse:collapse;margin-top:24px;font-size:13px}
    table.data th{background:#f1f5f9;text-align:left;padding:10px;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#475569}
    table.data td{padding:10px;border-bottom:1px solid #e2e8f0}
    table.data tr.itemdisc td{border-bottom:none;color:#64748b;font-size:12px;padding:2px 10px}
    table.data tr.itemdisc td:first-child{padding-left:24px}
    table.data tr.itemdisc.final td{color:#0f172a;font-weight:600;border-bottom:1px solid #e2e8f0;padding-bottom:8px}
    table.data tr.curnote td{border-bottom:none;color:#64748b;font-size:11px;padding:4px 10px 0;text-align:right}
    table.data td.num{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums}
    .right{text-align:right}
    .totals{margin-top:16px;width:280px;margin-left:auto;font-size:13px}
    .totals tr td{padding:5px 0}
    .totals .grand td{border-top:2px solid #0f172a;font-size:18px;font-weight:800;padding-top:10px;color:${accent}}
    .section{margin-top:20px}
    .section h3{font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#64748b;margin-bottom:6px}
    .box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px;font-size:12px}
    .notes{margin-top:18px;font-size:12px;color:#475569}
    .footer{margin-top:40px;border-top:1px solid #e2e8f0;padding-top:16px;text-align:center;font-size:11px;color:#94a3b8}
    .footer .brand{color:${accent};font-weight:700}
    .no-print-bar{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;margin-bottom:18px;display:flex;align-items:center;justify-content:space-between;gap:12px;font-size:13px;color:#475569}
    .no-print-bar button{background:${accent};color:#fff;border:0;border-radius:8px;padding:10px 20px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;white-space:nowrap;flex-shrink:0}
    .no-print-bar button:hover{opacity:.9}
    @media print{
      .no-print-bar{display:none !important}
      .page{padding:16px;max-width:100%}
      /* Multipage hygiene: no orphaned section headings, table headings repeat, rows never split mid-row. */
      .section{break-inside:avoid-page;page-break-inside:avoid}
      .section h3{break-after:avoid-page;page-break-after:avoid}
      table.data thead{display:table-header-group}
      table.data tr{break-inside:avoid;page-break-inside:avoid}
      @page{
        margin:${(footerNote || pageNumbers) ? "12mm 12mm 18mm 12mm" : "12mm"};
        ${footerNote ? `@bottom-left{content:"${cssStr(footerNote)}";font-size:8.5pt;color:#94a3b8;font-family:Helvetica,Arial,sans-serif}` : ""}
        ${pageNumbers ? `@bottom-right{content:"Page " counter(page) " of " counter(pages);font-size:8.5pt;color:#94a3b8;font-family:Helvetica,Arial,sans-serif}` : ""}
      }
    }
    ${wrapTitle ? `.doc{flex:0 1 auto;min-width:0}.doc h2{overflow-wrap:break-word}` : ""}
  </style></head><body>
  <div class="page">
    ${printButton ? `<div class="no-print-bar"><span>This window won't print or save automatically in every browser — use this button if nothing happens.</span><button type="button" onclick="window.print()">Print Now</button></div>` : ""}
    <div class="head">
      <div class="biz">${logo}<div>
        <h1>${esc(b.name || "NEEC POS")}</h1>
        ${b.legal_name ? `<p>${esc(b.legal_name)}</p>` : ""}
        ${b.address ? `<p>${esc(b.address)}${b.parish ? ", " + esc(b.parish) : ""}</p>` : ""}
        ${b.phone ? `<p>Tel: ${esc(b.phone)}</p>` : ""}
        ${b.email ? `<p>${esc(b.email)}</p>` : ""}
        ${b.trn ? `<p>TRN: ${esc(b.trn)}</p>` : ""}
      </div></div>
      <div class="doc">
        <h2>${esc(docTitle)}</h2>
        ${docRef ? `<div class="ref">${esc(docRef)}</div>` : ""}
        <table style="margin-top:10px;margin-left:auto;font-size:12px">${metaHtml}</table>
      </div>
    </div>
    ${body}
    ${plain ? "" : `${((showBank && bank.account_number) || recv.return_policy) ? `<div class="section"><div class="box">
       ${showBank && bank.account_number ? `<div><strong>Bank Transfer:</strong> ${esc(bank.bank_name || "")} · A/C ${esc(bank.account_name || "")} ${esc(bank.account_number || "")} ${bank.branch ? "· " + esc(bank.branch) : ""}</div>` : ""}
       ${recv.return_policy ? `<div style="margin-top:6px;color:#64748b">${esc(recv.return_policy)}</div>` : ""}
     </div></div>` : ""}
    <div class="footer">
      ${recv.footer ? `<div style="margin-bottom:6px;color:#475569">${esc(recv.footer)}</div>` : ""}
      Powered by <span class="brand">NEEC POS</span> · Modular Business Management Platform
    </div>`}
  </div>
  ${_preview ? "" : `<script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>`}
  </body></html>`;

  if (_preview) return html;
  if (inline && printInline(html)) return;
  const w = win || window.open("", "_blank", "width=900,height=1000");
  if (!w) { alert("Please allow pop-ups to print/download documents."); return; }
  // `win` may already have an open document.write() stream (e.g. a caller's placeholder left
  // open) — document.open() resets/truncates that stream so this write REPLACES it instead of
  // appending to it. Safe to call unconditionally: on a freshly-opened blank window it's a no-op.
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// Same-tab print: the document is loaded into a hidden iframe and its own onload → window.print() runs there.
// Returns false when the iframe path cannot be used so the caller can fall back to the popup.
function printInline(html) {
  try {
    document.getElementById("neec-print-frame")?.remove();
    const f = document.createElement("iframe");
    f.id = "neec-print-frame"; f.setAttribute("aria-hidden", "true"); f.title = "print";
    f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none";
    document.body.appendChild(f);
    const d = f.contentWindow?.document;
    if (!d) { f.remove(); return false; }
    const id = `inline-${Date.now()}-${++printSeq}`, t0 = performance.now(); logPrint(id, "open");   // same lifecycle names as the popup path; diagnostics only
    f.addEventListener("load", () => {
      logPrint(id, "opened"); try { diagPrintDuration(performance.now() - t0); } catch { /* never */ }
      try { f.contentWindow.addEventListener("beforeprint", () => logPrint(id, "beforeprint")); f.contentWindow.addEventListener("afterprint", () => logPrint(id, "afterprint")); } catch { /* never */ }
    }, { once: true });
    d.open(); d.write(html); d.close();
    f.contentWindow.addEventListener("afterprint", () => setTimeout(() => f.remove(), 500));
    return true;
  } catch { document.getElementById("neec-print-frame")?.remove(); return false; }
}

// Thermal receipt (80mm / 58mm). Monochrome centred logo, single-column stacked
// layout, high-contrast bold text, fixed-width item columns so long product names
// wrap without breaking Qty/Unit/Amount alignment. Sized @page for roll paper.
function printThermal({ b, docTitle, docRef, meta, body, format, showLogo, monoLogo, plain = false, inline = false, onDone = null, _preview = false }) {
  const is58 = format === "thermal58";
  const widthMm = is58 ? 58 : 80;
  const px = is58 ? 210 : 300;
  // Per-width type + column tuning.
  const cfg = is58
    ? { fBase: 10, fH1: 14, fTitle: 12, fMeta: 10, fFoot: 9, nameW: "40%", qtyW: "10%", unitW: "25%", amtW: "25%", grandPlus: 2 }
    : { fBase: 12, fH1: 17, fTitle: 15, fMeta: 11, fFoot: 10, nameW: "46%", qtyW: "10%", unitW: "22%", amtW: "22%", grandPlus: 3 };
  const recv = b.receipt_info || {};
  const metaLines = meta.filter((m) => m[1]).map((m) => `<div class="row"><span class="lbl">${esc(m[0])}</span><span class="val">${esc(m[1])}</span></div>`).join("");
  const logo = (showLogo && monoLogo)
    ? `<img src="${esc(monoLogo)}" style="max-width:64%;max-height:78px;object-fit:contain;filter:grayscale(1) contrast(1.4);margin:0 auto 4px;display:block" />` : "";
  const contact = plain ? "" : [b.address && (b.address + (b.parish ? ", " + b.parish : "")), b.phone && ("Tel: " + b.phone), b.email, b.website, b.trn && ("TRN: " + b.trn)].filter(Boolean).map((l) => `<div>${esc(l)}</div>`).join("");
  const h1Wrap = plain ? "overflow-wrap:normal;word-break:normal;hyphens:none;letter-spacing:0" : "overflow-wrap:anywhere";
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(docTitle)} ${esc(docRef)}</title>
  <style>
    @page{size:${widthMm}mm auto;margin:3mm}
    *{box-sizing:border-box;font-family:'Courier New',monospace;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    html,body{margin:0;color:#000;background:#fff;width:${px}px;font-weight:700;-webkit-font-smoothing:none}
    .r{width:${px}px;padding:0 4px}
    .c{text-align:center}
    .right{text-align:right}
    h1{font-size:${cfg.fH1}px;font-weight:800;margin:2px 0;letter-spacing:.4px;line-height:1.25;${h1Wrap}}
    .muted{font-size:${cfg.fMeta}px;line-height:1.45;font-weight:700;color:#000}
    .title{font-weight:800;text-transform:uppercase;font-size:${cfg.fTitle}px;letter-spacing:1.5px;margin:8px 0 1px}
    .ref{font-size:${cfg.fMeta + 1}px;font-weight:800;margin-bottom:2px}
    .hr{border-top:1px dashed #000;margin:6px 0}
    .hr.solid{border-top:2px solid #000}
    .row{display:flex;justify-content:space-between;font-size:${cfg.fMeta}px;gap:10px;line-height:1.65;font-weight:700;color:#000}
    .row .lbl{white-space:nowrap}
    .row .val{text-align:right;font-weight:800}
    table.data{width:100%;border-collapse:collapse;font-size:${cfg.fBase}px;table-layout:fixed;margin-top:2px}
    table.data th{display:table-cell;text-align:left;font-size:${cfg.fBase - 1}px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;border-bottom:1.5px solid #000;padding:2px 0}
    table.data td{padding:3px 0;vertical-align:top;word-break:break-word;font-weight:700;color:#000}
    table.data th.right,table.data td.right{text-align:right}
    table.data tr.itemdisc td{color:#000;font-weight:700;padding:1px 0;border:none;word-break:normal}
    table.data tr.itemdisc td:first-child{padding-left:8px}
    table.data tr.itemdisc.final td{padding-bottom:3px}
    table.data th:nth-child(1),table.data td:nth-child(1){width:${cfg.nameW};padding-right:4px}
    table.data th:nth-child(2),table.data td:nth-child(2){width:${cfg.qtyW};white-space:nowrap;word-break:normal;padding-right:3px}
    table.data th:nth-child(3),table.data td:nth-child(3){width:${cfg.unitW};padding-left:3px}
    table.data th:nth-child(4),table.data td:nth-child(4){width:${cfg.amtW};padding-left:3px}
    /* Money cells are numeric only (currency context printed once under the header); never wrap mid-number. */
    table.data td.num,.totals td.right,.totals td.num{white-space:nowrap;word-break:normal;overflow-wrap:normal;font-variant-numeric:tabular-nums}
    ${is58 ? `table.data td.num.l{font-size:${cfg.fBase - 1}px;letter-spacing:-.3px}` : ""}
    table.data td div{color:#000}
    table.data tr.curnote td{padding:1px 0 2px;font-size:${cfg.fBase - 2}px;font-weight:700;text-align:right;letter-spacing:.3px;border-bottom:1px dashed #000}
    .totals{width:100%;border-collapse:collapse;font-size:${cfg.fBase}px;margin-top:6px}
    .totals td{padding:2px 0;font-weight:700;color:#000}
    .totals td.right,.totals .right{text-align:right}
    .totals .grand td{font-weight:800;font-size:${cfg.fBase + cfg.grandPlus}px;border-top:2px solid #000;padding-top:5px}
    .totals tr.cash td{padding-top:3px}
    .totals tr.saved td{font-size:${cfg.fFoot}px;text-align:center;padding-top:4px}
    .foot{font-size:${cfg.fFoot}px;text-align:center;margin-top:8px;line-height:1.5;font-weight:700;color:#000}
    @media print{html,body{width:${px}px}}
  </style></head><body>
  <div class="r c">
    ${logo}
    <h1>${esc(b.name || "NEEC POS")}</h1>
    <div class="muted">${contact}</div>
    <div class="title">${esc(docTitle)}</div>
    ${docRef ? `<div class="ref">${esc(docRef)}</div>` : ""}
  </div>
  <div class="r">
    <div class="hr"></div>
    ${metaLines}
    <div class="hr"></div>
    ${body}
    ${plain ? "" : `${recv.return_policy ? `<div class="hr"></div><div class="muted c">${esc(recv.return_policy)}</div>` : ""}
    <div class="foot">
      ${recv.footer ? `<div>${esc(recv.footer)}</div>` : ""}
      <div class="hr"></div>Powered by NEEC POS
    </div>`}
  </div>
  ${_preview ? "" : `<script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>`}
  </body></html>`;
  if (_preview) return html;
  if (inline && printInline(html)) return;
  openReceiptWindow(html, px, onDone);
}

// Stage 3 — Preview Quote (and any future non-printing preview). Returns the exact
// HTML `printDoc` would otherwise print, with no print trigger and no window/tab
// opened. Thin wrapper so callers never touch the internal `_preview` flag directly.
export function previewHtml(opts) {
  return printDoc({ ...opts, inline: false, onDone: null, _preview: true });
}

// Thermal receipt window, isolated from the POS. Opened from a Blob URL with `noopener`, so the receipt runs in its OWN
// renderer: a print() that never returns (stuck preview / system dialog / driver) can no longer pause the POS tab and its
// dialogs. The POS gets no handle to a noopener window, so the receipt reports its lifecycle over a same-origin
// BroadcastChannel; it closes itself only after `afterprint` (never while a print job is active). Legacy same-renderer
// popup is kept as the fallback when Blob URLs / BroadcastChannel are unavailable.
const PRINT_CHANNEL = "neec-print";
const PRINT_OPEN_GRACE_MS = 3000;       // no "opened" signal by then → popup was blocked
const PRINT_CLEANUP_BOUND_MS = 15 * 60 * 1000;
let printSeq = 0;
export const printLog = [];
if (typeof window !== "undefined") window.__neecPrintLog = printLog;   // support diagnostics: last 40 receipt-window lifecycle events
const logPrint = (id, t) => { printLog.push({ id, t, at: Date.now() }); if (printLog.length > 40) printLog.shift(); try { diagPrint(id, t); } catch { /* never */ } };

function openReceiptWindow(html, px, onDone) {
  const features = `width=${px + 60},height=800`;
  if (typeof BroadcastChannel !== "function" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    const w = window.open("", "_blank", features);
    if (!w) { alert("Please allow pop-ups to print receipts."); return; }
    w.document.write(html); w.document.close(); return;
  }
  const id = `${Date.now()}-${++printSeq}`;
  const lifecycle = `<base href="${esc(window.location.origin)}/"><script>(function(){var id=${JSON.stringify(id)},ch=new BroadcastChannel(${JSON.stringify(PRINT_CHANNEL)});` +
    `function s(t){try{ch.postMessage({id:id,t:t})}catch(e){}}s("opened");` +
    `window.addEventListener("beforeprint",function(){s("beforeprint")});` +
    `window.addEventListener("afterprint",function(){s("afterprint");setTimeout(function(){window.close()},150)});` +
    `window.addEventListener("pagehide",function(){s("closed")});})();</script>`;
  const url = URL.createObjectURL(new Blob([html.replace('<meta charset="utf-8">', '<meta charset="utf-8">' + lifecycle)], { type: "text/html;charset=utf-8" }));
  const ch = new BroadcastChannel(PRINT_CHANNEL);
  let opened = false; const tOpen = performance.now();
  const cleanup = () => { clearTimeout(openGuard); clearTimeout(bound); ch.close(); URL.revokeObjectURL(url); };
  ch.onmessage = (e) => {
    const m = e.data || {};
    if (m.id !== id) return;
    logPrint(id, m.t);
    if (m.t === "opened" && !opened) { opened = true; try { diagPrintDuration(performance.now() - tOpen); } catch { /* never */ } }
    if (m.t === "afterprint" || m.t === "closed") { try { window.focus(); } catch { /* ignore */ } onDone?.(m.t); }
    if (m.t === "afterprint") setTimeout(cleanup, 2000);   // receipt window closes itself ~150 ms after afterprint; pagehide may not deliver
    if (m.t === "closed") cleanup();
  };
  const openGuard = setTimeout(() => { if (!opened) { logPrint(id, "blocked"); cleanup(); alert("Please allow pop-ups to print receipts."); } }, PRINT_OPEN_GRACE_MS);
  const bound = setTimeout(() => { logPrint(id, "cleanup_bound"); cleanup(); }, PRINT_CLEANUP_BOUND_MS);
  logPrint(id, "open");
  window.open(url, "_blank", `noopener,${features}`);
}

// Thermal (80mm / 58mm) QUOTATION body. Dedicated stacked layout — not the 4-column
// receipt table — so long product names wrap naturally on narrow roll paper:
//   Product name
//   Qty × Unit .............. Line Total
//   Discount / Line Total rows when discounted
// Money is right-aligned and never wraps mid-number. Rendered inside printThermal()'s
// shared header (logo / business / QUOTATION title / ref / meta rows).
export function quotationThermalBody(q, cur, { taxLabel = "Tax", format = "thermal80" } = {}) {
  const is58 = format === "thermal58";
  const f = is58 ? 10 : 12;
  const line = (lbl, val, cls = "") => `<div class="row qr ${cls}"><span class="lbl">${lbl}</span><span class="val num">${val}</span></div>`;
  const items = (q.items || []).map((i) => {
    const orig = i.price * i.quantity, lineDisc = (i.discount || 0) * i.quantity, hasDisc = lineDisc > 0.005, final = orig - lineDisc;
    let out = `<div class="qi"><div class="qn">${esc(i.name)}${i.pricing_source === "bulk_tier" ? ` <span class="qtag">Bulk Price</span>` : ""}</div>`;
    out += line(`${i.quantity} × ${pnum(i.price)}`, pnum(orig));
    if (hasDisc) {
      const pct = i.discount_effective_pct != null ? i.discount_effective_pct : (orig > 0 ? lineDisc / orig * 100 : 0);
      const pctTxt = (Math.round(pct * 100) / 100).toString();
      const neg = i.negotiated_price ?? (i.discount_type === "override" ? i.discount_value : null);
      const lbl = i.discount_type === "override" && neg != null ? `Negotiated price ${pnum(neg)}` : `Item Discount ${pctTxt}%`;
      out += line(lbl, `−${pnum(lineDisc)}`, "qd") + line("Line Total", pnum(final), "qf");
    }
    return out + `</div>`;
  }).join("");
  const saleDisc = Number(q.discount || 0);
  const delivDisc = Number(q.delivery_discount || 0);
  const totals = totalsBlock({
    subtotal: q.subtotal, tax: q.tax, total: q.total, taxLabel, deliveryFee: q.delivery_fee, deliveryTax: q.delivery_tax,
    ...(saleDisc > 0.005 ? { discountAmount: saleDisc, originalSubtotal: Number(q.subtotal || 0) + saleDisc, discountLabel: "Quote Discount" } : {}),
    ...(delivDisc > 0.005 ? { deliveryDiscount: delivDisc, deliveryOriginal: Number(q.delivery_fee || 0) + delivDisc } : {}),
    ...(q.tax_exempt ? { taxExemptNote: `Tax Exempt${q.tax_exempt_reason ? " — " + q.tax_exempt_reason : ""}` } : {}),
  }, cur);
  const note = (lbl, txt) => (txt ? `<div class="qnote"><b>${lbl}:</b> ${esc(txt)}</div>` : "");
  return `<style>
    .qwarn{text-align:center;font-weight:800;font-size:${f}px;letter-spacing:1px;border:2px solid #000;padding:3px 2px;margin:2px 0 6px;line-height:1.3}
    .qcur{font-size:${f - 2}px;text-align:right;letter-spacing:.3px;border-bottom:1px dashed #000;padding-bottom:2px;margin-bottom:2px}
    .qi{padding:3px 0;border-bottom:1px dashed #000}
    .qn{font-size:${f}px;font-weight:800;line-height:1.35;overflow-wrap:anywhere;word-break:normal}
    .qtag{font-weight:700;font-size:${f - 2}px}
    .row.qr{font-size:${f}px;line-height:1.5;gap:8px}
    .row.qr .lbl{white-space:normal;overflow-wrap:anywhere}
    .row.qr .val{white-space:nowrap;font-variant-numeric:tabular-nums;flex:0 0 auto}
    .row.qd .lbl,.row.qd .val{font-size:${f - 1}px;padding-left:6px}
    .row.qf .val{text-decoration:underline}
    .qnote{font-size:${f - 1}px;line-height:1.4;margin-top:5px;overflow-wrap:anywhere}
    .qvalid{text-align:center;font-size:${f - 1}px;margin-top:6px;line-height:1.4}
  </style>
  <div class="qwarn" data-doc="quotation-thermal">QUOTATION — NOT A RECEIPT</div>
  <div class="qcur">Prices in ${esc(curCode(cur))}</div>
  ${items}
  ${totals}
  ${note("Delivery", q.delivery_note)}${note("Notes", q.notes)}
  <div class="qvalid">This quotation is valid until <b>${esc(q.valid_until_text || "")}</b>.<br/>Prices may change after this date. No payment has been received.</div>`;
}

const METHOD_RECEIPT = { take_now: "Collected Now", pickup: "Awaiting Pickup", delivery: "Awaiting Delivery" };

export function itemsTable(items, cur, { showFulfillment = false } = {}) {
  const rows = items.map((i) => {
    const orig = i.price * i.quantity;
    const lineDisc = (i.discount || 0) * i.quantity;
    const hasDisc = lineDisc > 0.005;
    const final = orig - lineDisc;
    const bulkTag = i.pricing_source === "bulk_tier" ? `<div style="font-size:10px;color:#475569">Bulk Price</div>` : "";
    const tag = bulkTag + (showFulfillment && i.fulfillment ? `<div style="font-size:10px;color:#475569">${esc(METHOD_RECEIPT[i.fulfillment] || "")}</div>` : "");
    let out = `<tr>
      <td>${esc(i.name)}${tag}</td>
      <td class="right">${i.quantity}</td>
      ${numTd(i.price)}
      ${numTd(orig, "amt")}
    </tr>`;
    if (hasDisc) {
      const pct = i.discount_effective_pct != null ? i.discount_effective_pct : (orig > 0 ? lineDisc / orig * 100 : 0);
      const pctTxt = (Math.round(pct * 100) / 100).toString();
      if (i.discount_type === "override" && i.negotiated_price != null) {
        out += `<tr class="itemdisc"><td colspan="3">Negotiated ${pnum(i.negotiated_price)} (was ${pnum(i.price)}, −${pctTxt}%)</td><td class="right num">−${pnum(lineDisc)}</td></tr>
        <tr class="itemdisc final"><td colspan="3">Line Total (${i.quantity} × ${pnum(i.negotiated_price)})</td><td class="right num">${pnum(final)}</td></tr>`;
      } else {
        out += `<tr class="itemdisc"><td colspan="3">Item Discount ${pctTxt}%</td><td class="right num">−${pnum(lineDisc)}</td></tr>
        <tr class="itemdisc final"><td colspan="3">Line Total</td><td class="right num">${pnum(final)}</td></tr>`;
      }
    }
    return out;
  }).join("");
  return `<table class="data"><thead><tr><th>Item</th><th class="right">Qty</th><th class="right">Unit</th><th class="right">Amount</th></tr></thead><tbody>
    <tr class="curnote"><td colspan="4" data-testid="receipt-currency-note">Prices in ${esc(curCode(cur))}</td></tr>${rows}</tbody></table>`;
}

// Customer-facing compact pickup reference. No per-line Bought/Done/Left table —
// that lives on the Fulfillment / Yard Ticket. Never exposes stock, reservations or sourcing.
export function pickupBlock(inv) {
  if (!inv?.pickup_reference) return "";
  const src = (inv.lines || inv.items || []).filter((l) => (l.fulfillment || "pickup") !== "take_now");
  const units = src.reduce((a, l) => a + Number(l.purchased_qty ?? l.quantity ?? 0), 0);
  const label = inv.fulfillment_label || "Awaiting Pickup";
  const anyDelivery = src.some((l) => l.fulfillment === "delivery");
  const anyPickup = src.some((l) => (l.fulfillment || "pickup") === "pickup");
  const done = ["picked_up", "delivered", "fulfilled", "closed", "force_closed"].includes(String(inv.fulfillment_status || "").toLowerCase());
  const title = done ? "PICKUP COMPLETE" : anyDelivery && anyPickup ? "PICKUP & DELIVERY REQUIRED" : anyDelivery ? "DELIVERY REQUIRED" : "PICKUP REQUIRED";
  const row = (k, v) => `<div class="row"><span class="lbl">${k}</span><span class="val">${esc(v)}</span></div>`;
  return `<div class="section pickup-ref" style="margin-top:12px;border:2px solid #000;padding:6px 8px;border-radius:6px" data-doc="customer-pickup-ref">
    <div style="text-align:center;font-weight:800;letter-spacing:2px;font-size:13px;margin-bottom:3px">${title}</div>
    ${row("Pickup Ref", inv.pickup_reference)}
    ${row("Status", label)}
    ${row("Items", `${src.length} line${src.length === 1 ? "" : "s"} · ${units} unit${units === 1 ? "" : "s"}`)}
    <div style="text-align:center;font-size:10px;margin-top:5px">Please present this receipt or pickup reference when collecting your goods.</div>
  </div>${pickupQrBlock(inv)}`;
}

// Scannable code — front of receipt, below the fulfillment section, above the footer.
// Rendered only when the backend attached pickup_qr (QR/Barcode mode + outstanding reference).
export function pickupQrBlock(inv) {
  if (!inv?.pickup_qr || !inv?.pickup_reference) return "";
  return `<div class="pickup-qr" style="text-align:center;margin:18px 0 14px;padding:10px 0;page-break-inside:avoid;break-inside:avoid">
    <img src="${inv.pickup_qr}" alt="Pickup QR" style="width:150px;height:150px;image-rendering:pixelated;display:block;margin:0 auto 8px;background:#fff" />
    <div style="font-family:monospace;font-weight:800;font-size:15px;letter-spacing:1px">${esc(inv.pickup_reference)}</div>
    <div style="font-size:10px;margin-top:3px">Scan or quote this reference at the pickup counter</div>
  </div>`;
}

// Internal Fulfillment / Yard Ticket body. Operational only: Bought / Done / Left per
// line with a release marker and notes line. No prices, totals, discounts or cost.
// Always rendered from the CURRENT fulfillment record passed in (purchased_qty / picked_up_qty).
export function yardTicket(inv) {
  if (!inv) return "";
  const src = (inv.lines || inv.items || []).filter((l) => (l.fulfillment || "pickup") !== "take_now");
  const rows = src.map((l) => {
    const bought = l.purchased_qty ?? l.quantity;
    const done = l.picked_up_qty || 0;
    const left = Math.max(0, bought - done);
    const method = l.fulfillment || "pickup";
    const box = left <= 0 ? "&#9745;" : "&#9744;";
    const tag = method === "delivery" ? `<div style="font-size:9px">Delivery</div>` : "";
    return `<tr class="yl">
      <td class="box">${box}</td>
      <td>${esc(l.name)}${tag}</td>
      <td class="right">${bought}</td><td class="right">${done}</td><td class="right"><b>${left}</b></td>
    </tr><tr class="note"><td></td><td colspan="4">Notes: ____________________</td></tr>`;
  }).join("");
  const sign = (k) => `<div class="row" style="margin-top:12px"><span class="lbl">${k}:</span><span class="val" style="flex:1;border-bottom:1px solid #000"></span></div>`;
  return `<style>
    table.yard{width:100%;border-collapse:collapse;table-layout:fixed;font-size:inherit}
    table.yard th{text-align:left;font-weight:800;text-transform:uppercase;font-size:90%;border-bottom:1.5px solid #000;padding:2px 0}
    table.yard td{padding:3px 0;vertical-align:top;word-break:break-word;font-weight:700}
    table.yard th.right,table.yard td.right{text-align:right;white-space:nowrap}
    table.yard .box{width:16px;font-size:14px;text-align:left}
    table.yard th:nth-child(3),table.yard th:nth-child(4),table.yard th:nth-child(5),table.yard td:nth-child(3),table.yard td:nth-child(4),table.yard td:nth-child(5){width:15%}
    table.yard tr.note td{padding:0 0 4px;font-size:85%;font-weight:700;border-bottom:1px dashed #999}
  </style>
  <div data-doc="yard-ticket">
    <div class="muted c" style="margin-bottom:4px">INTERNAL — staff hand-over document. Not a receipt.</div>
    <table class="yard"><thead><tr><th></th><th>Product</th><th class="right">Bought</th><th class="right">Done</th><th class="right">Left</th></tr></thead><tbody>${rows}</tbody></table>
    ${inv.delivery_note ? `<div class="muted" style="margin-top:6px">Delivery: ${esc(inv.delivery_note)}</div>` : ""}
    <div class="hr"></div>
    ${sign("Released By")}${sign("Received By")}${sign("Date / Time")}${sign("Customer / Vehicle / Notes")}
  </div>`;
}

const PAY_LABEL = { paid: "Paid", partial: "Partially Paid", partially_paid: "Partially Paid", credit: "Credit", unpaid: "Unpaid", on_account: "Credit" };
const payStatus = (inv) => { const s = inv.payment_status; if (s) return PAY_LABEL[String(s).toLowerCase()] || s; return inv.is_credit ? "Credit" : "Paid"; };

// Print the Fulfillment / Yard Ticket as its own document (thermal 80/58 or standard).
// Separate entry point so a future yard printer can be routed independently of the receipt printer.
export function printYardTicket(inv, business = {}, format = "thermal80", { inline = false } = {}) {
  if (!inv?.pickup_reference) return;
  const created = inv.created_at || inv.sale_date;
  printDoc({ business, docTitle: "Fulfillment / Yard Ticket", docRef: inv.pickup_reference, format, receipt: true, inline,
    meta: [["Invoice", inv.invoice_number], ["Customer", inv.customer_name || "Walk-in"], ["Payment", payStatus(inv)],
      ["Fulfillment", inv.fulfillment_label || "Awaiting Pickup"], ["Created", created ? parseDate(created).toLocaleString() : new Date().toLocaleString()]],
    body: yardTicket(inv) });
}

// Payment section rules:
//  - fully paid cash sale with a known tender  -> Cash Received / Change Due (no "Paid" / "Balance Due 0.00")
//  - split tenders                             -> one row per tender (stored breakdown); Cash Received / Change Due only if cash was over-tendered
//  - outstanding balance (partial / credit)    -> Paid / Balance Due
//  - non-cash or historical sale without a stored tender -> Paid only (never guess a tender)
export function totalsBlock({ subtotal, tax, total, taxLabel = "Tax", paid, balance, originalSubtotal, discountAmount, discountLabel = "Discount", postedToCredit, tenders, tendered, change, paymentMethod, deliveryFee, deliveryTax, deliveryLabel = "Delivery", deliveryOriginal, deliveryDiscount, taxExemptNote, items, walletLabel = "Wallet" }, cur) {
  const saved = bulkSavings(items);
  const m = (n, strong = false) => `<td class="right num">${strong ? "<strong>" : ""}${rmoney(n, cur)}${strong ? "</strong>" : ""}</td>`;
  const row = (lbl, n, cls = "", strong = false) => `<tr${cls ? ` class="${cls}"` : ""}><td>${strong ? "<strong>" : ""}${esc(lbl)}${strong ? "</strong>" : ""}</td>${m(n, strong)}</tr>`;
  const hasTenders = !!(tenders && tenders.length);
  const tenderRows = hasTenders
    ? tenders.map((t) => {
        const lbl = t.method === "bank_transfer" ? ("Bank Transfer" + (t.confirmed ? "" : " (unconfirmed)")) : (t.method === "card" ? "Card" : t.method === "prepaid" ? walletLabel : "Cash");
        return row(lbl + (t.method === "bank_transfer" && t.reference ? " · " + t.reference : ""), t.amount);
      }).join("")
    : "";
  const outstanding = balance != null && balance > 0.005;
  const isCash = String(paymentMethod || "").toLowerCase() === "cash";
  let payRows = "";
  if (hasTenders) {
    const cashGiven = tenders.reduce((a, t) => a + (t.method === "cash" ? Number(t.cash_given ?? t.amount ?? 0) : 0), 0);
    const cashApplied = tenders.reduce((a, t) => a + (t.method === "cash" ? Number(t.amount || 0) : 0), 0);
    const chg = change != null ? Number(change) : Math.max(0, cashGiven - cashApplied);
    if (chg > 0.005) payRows += row("Cash Received", cashGiven, "cash") + row("Change Due", chg, "cash", true);
    if (outstanding) payRows += (paid > 0.005 ? row("Paid", paid) : "") + row("Balance Due", balance, "", true);
  } else if (!outstanding && isCash && tendered != null && Number(tendered) >= Number(total || 0) - 0.005 && Number(tendered) > 0) {
    payRows += row("Cash Received", tendered, "cash") + row("Change Due", change != null ? change : Math.max(0, Number(tendered) - Number(total || 0)), "cash", true);
  } else {
    if (paid != null && (paid > 0.005 || !outstanding)) payRows += row("Paid", paid);
    if (outstanding) payRows += row("Balance Due", balance, "", true);
  }
  return `<table class="totals" data-testid="receipt-totals">
    ${discountAmount ? `${row("Original Subtotal", originalSubtotal)}
    <tr><td>${esc(discountLabel)}</td><td class="right num">−${rmoney(discountAmount, cur)}</td></tr>` : ""}
    ${row("Subtotal", subtotal)}
    ${taxExemptNote ? `<tr><td colspan="2" style="font-size:11px">${esc(taxExemptNote)}</td></tr>` : (tax ? row(taxLabel, tax) : "")}
    ${deliveryDiscount ? `${row(`${deliveryLabel} (before discount)`, deliveryOriginal)}
    <tr><td>${esc(deliveryLabel)} Discount</td><td class="right num">−${rmoney(deliveryDiscount, cur)}</td></tr>` : ""}
    ${deliveryFee ? row(deliveryLabel, deliveryFee) : ""}
    ${deliveryTax ? row(`${deliveryLabel} ${taxLabel}`, deliveryTax) : ""}
    ${row("Total", total, "grand")}
    ${saved > 0.005 ? `<tr class="saved"><td colspan="2" style="text-align:center">You saved ${rmoney(saved, cur)} with bulk pricing</td></tr>` : ""}
    ${tenderRows}
    ${postedToCredit != null && postedToCredit > 0.005 && paid > 0.005 ? row("On account", postedToCredit) : ""}
    ${payRows}
  </table>`;
}


// Refund receipt body: only what was actually refunded. Delivery rows appear ONLY when delivery was refunded;
// delivery-only refunds print no item table. Wording is refund-specific ("Refunded", never "Paid").
export function refundBody(refund, cur, { taxLabel = "Tax", deliveryLabel = "Delivery", walletLabel = "Wallet" } = {}) {
  const items = refund.items || [];
  const m = (n, strong = false) => `<td class="right num">${strong ? "<strong>" : ""}${rmoney(n, cur)}${strong ? "</strong>" : ""}</td>`;
  const row = (lbl, n, cls = "", strong = false) => `<tr${cls ? ` class="${cls}"` : ""}><td>${strong ? "<strong>" : ""}${esc(lbl)}${strong ? "</strong>" : ""}</td>${m(n, strong)}</tr>`;
  const neg = (lbl, n) => `<tr><td>${esc(lbl)}</td><td class="right num">−${rmoney(n, cur)}</td></tr>`;
  const table = items.length ? itemsTable(items, cur) : "";
  const disc = Number(refund.sale_discount || 0);
  const net = refund.net_subtotal != null ? Number(refund.net_subtotal) : Number(refund.subtotal || 0) - disc;
  const dFee = Number(refund.delivery_fee || 0), dTax = Number(refund.delivery_tax || 0);
  const method = refund.payment_method || (refund.is_credit ? "Credit" : "Cash");
  return table + `<table class="totals" data-testid="refund-receipt-totals">
    ${items.length ? row("Items Refunded", refund.subtotal || 0) : ""}
    ${disc > 0.005 ? neg("Sale Discount Share", disc) : ""}
    ${items.length && disc > 0.005 ? row("Refundable", net) : ""}
    ${Number(refund.tax || 0) > 0.005 ? row(taxLabel, refund.tax) : ""}
    ${dFee > 0.005 ? row(`${deliveryLabel} Refunded`, dFee) : ""}
    ${dTax > 0.005 ? row(`${deliveryLabel} ${taxLabel}`, dTax) : ""}
    ${row("Refund Total", refund.total || 0, "grand", true)}
    ${(() => {
      const ws = Number(refund.prepaid_share ?? refund.prepaid_credited ?? 0), nw = Number(refund.non_wallet_share ?? 0);
      if (refund.is_credit) return `<tr><td>Refunded via</td><td class="right">Customer account (credit reversed)</td></tr>`;
      if (ws > 0.005 && nw > 0.005) return `<tr><td>Refunded to ${esc(walletLabel)}</td><td class="right num">${rmoney(ws, cur)}</td></tr><tr><td>Refunded via ${esc(method)}</td><td class="right num">${rmoney(nw, cur)}</td></tr>${refund.prepaid_balance_after != null ? `<tr><td>Wallet Balance Remaining</td><td class="right num">${rmoney(refund.prepaid_balance_after, cur)}</td></tr>` : ""}`;
      if (/prepaid|wallet/i.test(method)) return `<tr><td>Refunded to</td><td class="right">${esc(walletLabel)}</td></tr>${refund.prepaid_balance_after != null ? `<tr><td>Wallet Balance Remaining</td><td class="right num">${rmoney(refund.prepaid_balance_after, cur)}</td></tr>` : ""}`;
      return `<tr><td>Refunded via</td><td class="right">${esc(method)}</td></tr>`;
    })()}
    ${refund.include_tax === false ? `<tr><td colspan="2" style="font-size:11px;color:#475569">${esc(taxLabel)} not included in this refund</td></tr>` : ""}
  </table>`;
}
